<script>
import api from '@/helpers/api'
import { authComputed } from '@/state/helpers'
import { required, minValue, decimal } from 'vuelidate/lib/validators'
import Multiselect from 'vue-multiselect'

import CKEditor from '@ckeditor/ckeditor5-vue'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'

import Forms from '@/components/forms'

export default {
  name: 'product.sales',
  props: {
    product_id: { type: String, required: true }
  },
  components: { ckeditor: CKEditor.component },

  data: () => ({
    editor: ClassicEditor,
    isLoading: false,
    product: {},
    initial_product: {},
    price_history: []
  }),
  mounted () {
    this.onInit()
  },
  watch: {
    product_id: { handler: 'onInit', immediate: true }
  },
  computed: {
    ...authComputed,
    salesPriceChanged () {
      return this.initial_product.sales_price !== this.product.sales_price
    },
    customFormOptions () {
      let options = [{ value: null, text: this.$t('No Custom Form') }]
      Object.keys(Forms).forEach(form_type => {
        options.push({
          value: form_type,
          text: Forms[form_type].label
        })
      })
      return options
    }
  },
  validations: {
    product: {
      sales_price: {
        required,
        minValue: minValue(0),
        decimal
      }
    }
  },
  methods: {
    async onInit () {
      this.isLoading = true
      let item = await api.items.fetch({
        id: this.product_id
      })
      this.product = { ...item }
      this.initial_product = { ...item }

      let { items } = await api.itemsalesPriceAudits.fetchPage(
        this.product_id,
        {
          page: 1,
          pageSize: -1,
          orderBy: { created_at: 'desc' }
        }
      )

      this.price_history = [...items]
      this.isLoading = false
    },
    async onSave () {
      let valid = true

      if (valid) {
        const data = {
          ...this.product
        }

        try {
          this.isLoading = true
          let item = await api.items.update({
            ...data,
            id: this.product_id
          })
          /* if (this.salesPriceChanged)
            // TODO: create trigger in database (if x-hasura-user_id is available in trigger function)
            await api.itemsalesPriceAudits.create(this.product_id, {
              sales_price_before: this.initial_product.sales_price,
              sales_price_after: data.sales_price,
              user_id: this.currentUser.id
            }) */
          this.$bvToast.toast(`Product info was saved!`, {
            title: 'Changes Saved',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'success',
            autoHideDelay: 1000,
            appendToast: true
          })
          this.$emit('update')
          this.onInit()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false

          this.$bvToast.toast(`${error}!`, {
            title: 'Error',
            solid: true,
            toaster: 'b-toaster-top-center',
            variant: 'danger',
            autoHideDelay: 1000,
            appendToast: true
          })
        }
      }
    }
  }
}
</script>
<template>
  <g-overlay :show="isLoading">
    <b-card>
      <h4 class="card-title border-bottom pb-3 mb-4">Webshop Information</h4>
      <b-form-group
        label="Available in Shop"
        label-for="product_shop"
        label-cols-lg="2"
        label-cols-sm="3"
      >
        <app-shop-select v-model="product.shops" size="sm" multiple />
      </b-form-group>
      <b-form-group
        label="Description"
        label-for="product_description"
        label-cols-lg="2"
        label-cols-sm="3"
      >
        <ckeditor
          v-model="product.description"
          :editor="editor"
          id="product_description"
        ></ckeditor>
      </b-form-group>
      <b-form-group
        label="Tags "
        label-for="product_tags"
        label-cols-lg="2"
        label-cols-sm="3"
      >
        <g-tag-input
          v-model="product.tags"
          size="sm"
          allow-create
          placeholder="Enter a tag and press Enter..."
        />
      </b-form-group>

      <g-select
        label="Custom Form"
        v-model="product.custom_form"
        label-cols-lg="2"
        label-cols-sm="3"
        :options="customFormOptions"
      />

      <b-form-group
        label-for="webshop_price_hidden"
        label-cols-lg="2"
        label-cols-sm="3"
      >
        <template v-slot:label> Sales Price Hidden </template>
        <b-form-checkbox
          switch
          size="lg"
          id="webshop_price_hidden"
          v-model="product.webshop_price_hidden"
          name="webshop_price_hidden"
          class="py-2"
        ></b-form-checkbox>
      </b-form-group>

      <div class="d-flex justify-content-end border-top pt-3">
        <b-btn
          class="btn-label"
          variant="primary"
          v-on:click="onSave"
          :disabled="$v.$invalid"
        >
          <i class="bx bx-save label-icon"></i>Save Changes
        </b-btn>
      </div>
    </b-card>

    <b-modal
      id="price-history-modal"
      title="Sales Price History"
      body-class="modal-height-sm"
      size="sm"
      ok-only
      v-if="price_history.length > 0"
    >
      <template v-for="p in price_history">
        <div :key="p.id" class="d-flex flex-column py-2">
          <div class="d-flex justify-content-between mb-0">
            <span>New Price: </span
            ><span>{{ p.sales_price_after }}<small>EUR</small></span>
          </div>
          <div class="d-flex justify-content-between mb-0">
            <span>Old Price:</span>
            <span>
              {{ p.sales_price_before }}
              <small>EUR</small>
            </span>
          </div>
          <div class="d-flex justify-content-between mt-1">
            <small>{{ p.created_at | moment('DD/MM/YYYY HH:mm') }}</small>
            <small v-if="p.user">by {{ p.user.name }}</small>
          </div>
        </div>
      </template>
    </b-modal>
  </g-overlay>
</template>

<style lang="scss">
.modal-height-sm {
  max-height: 300px;
  overflow-y: auto;
}
</style>
